import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import "./StudentProfile.css";
import initialStudentBannerImage from '../assets/novas-banner-template.jpeg';
import { getStudentData } from '../assets/data';
import { getLessonListForStudent, getLessonsFromLocalStorage } from '../utils/lessonsService';
import DictionaryProfile from './DictionaryProfile';
import PaymentModal from './StudentProfile/PaymentModal.jsx';
import LessonsTab from './StudentProfile/LessonsTab.jsx';
import ScheduleTab from './StudentProfile/ScheduleTab.jsx';
import ProfileDetailsForm from './StudentProfile/ProfileDetailsForm.jsx';
import HomeworkTab from './StudentProfile/HomeworkTab.jsx';
import MaterialsTab from './StudentProfile/MaterialsTab.jsx';
import CustomModal from './StudentProfile/CustomModal.jsx';
import CopyLessonModal from './StudentProfile/CopyLessonModal.jsx';
import AddLessonModal from './StudentProfile/AddLessonModal.jsx';
import EditScheduleLessonModal from './StudentProfile/EditScheduleLessonModal.jsx';
import MaterialPreviewModal from './StudentProfile/MaterialPreviewModal.jsx';
import InviteLinkModal from './StudentProfile/InviteLinkModal.jsx';
import { computeWeekSchedule } from './StudentProfile/scheduleWeek';
import StudentChatModal from './StudentChatModal.jsx';
import Tour from './Tour/Tour';
import TourTrigger from './Tour/TourTrigger';
import { STUDENT_TOUR_STEPS } from './Tour/tourSteps';
import { usePaymentBalance } from '../hooks/usePaymentBalance';
import { useMaterialsManager } from '../hooks/useMaterialsManager';
import { useHomeworkManager } from '../hooks/useHomeworkManager';
import { useLessonsManager } from '../hooks/useLessonsManager';
import { useAIRecommendations } from '../hooks/useAIRecommendations';
import { useScheduleLessonModals } from '../hooks/useScheduleLessonModals';
import { useStudentProfileLoader } from '../hooks/useStudentProfileLoader';
import { useProfileFormActions } from '../hooks/useProfileFormActions';
import { useScheduleSync } from '../hooks/useScheduleSync';
import { useScheduleTabActions } from '../hooks/useScheduleTabActions';
import { useStudentStats } from '../hooks/useStudentStats';
import { useStatCardTruncation } from '../hooks/useStatCardTruncation';
import { usePaymentModalActions } from '../hooks/usePaymentModalActions';
import { useLessonsTabStatusChange } from '../hooks/useLessonsTabStatusChange';
import useTeacherUnreadCounts from '../hooks/useTeacherUnreadCounts';
import { markChatRead } from '../utils/notificationCountsService';

const STUDENT_TOUR_SEEN_KEY = 'novas_tour_student_v1_seen';

// Dictionary functionality moved to DictionaryProfile component

function StudentProfile({ isNew }) {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const isCreatingNewStudent = studentId === "new" || isNew;
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const currentUser = React.useMemo(() => {
    try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch { return {}; }
  }, []);
  const { byStudent: unreadByStudent } = useTeacherUnreadCounts();

  const markDirty = () => {
    if (isCreatingNewStudent) setHasUnsavedChanges(true);
  };

  const confirmLoseChanges = (onProceed) => {
    if (!isCreatingNewStudent || !hasUnsavedChanges) {
      onProceed && onProceed();
      return true;
    }

    showCustomModalConfig({
      title: "Unsaved changes",
      message:
        "Your changes will be lost.\n\nClick Save (top bar) to save your changes.\n\nLeave this page?",
      type: "warning",
      showCancel: true,
      confirmText: "Leave",
      cancelText: "Stay",
      onConfirm: () => {
        onProceed && onProceed();
      },
    });

    return false;
  };

  // Warn on tab close/refresh
  useEffect(() => {
    if (!isCreatingNewStudent || !hasUnsavedChanges) return;
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      // Chrome requires returnValue to be set.
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isCreatingNewStudent, hasUnsavedChanges]);

  // Browser back / swipe-back guard.
  //
  // The sidebar/menu paths already route through window.__novasUnsavedChangesGuard,
  // but the browser back button (and the iOS / iPadOS / trackpad swipe-back
  // gesture) bypass that entirely — they fire popstate AFTER the history
  // entry is already popped. Trick: push a sentinel history entry on mount
  // so the first "back" lands on us, then in the popstate handler we
  // immediately re-push that sentinel and show the same Unsaved-changes
  // confirmation. If the user picks "Leave" we remove the handler and call
  // history.back() to actually navigate.
  useEffect(() => {
    if (!isCreatingNewStudent || !hasUnsavedChanges) return;

    const sentinel = { novasUnsavedGuard: true };
    window.history.pushState(sentinel, '', window.location.href);

    let handler;
    handler = () => {
      // We've already popped — re-trap immediately so the user stays here
      // until the modal is answered. Cheap, idempotent, no URL change.
      window.history.pushState(sentinel, '', window.location.href);
      showCustomModalConfig({
        title: 'Unsaved changes',
        message:
          'Your changes will be lost.\n\nClick Save (top bar) to save your changes.\n\nLeave this page?',
        type: 'warning',
        showCancel: true,
        confirmText: 'Leave',
        cancelText: 'Stay',
        onConfirm: () => {
          // Remove the listener BEFORE the back() call so the next popstate
          // isn't intercepted by us again.
          window.removeEventListener('popstate', handler);
          window.history.back();
        },
      });
    };

    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingNewStudent, hasUnsavedChanges]);

  // Hook sidebar/navigation clicks (centralized)
  useEffect(() => {
    if (!isCreatingNewStudent) return;
    window.__novasUnsavedChangesGuard = (onProceed) => confirmLoseChanges(onProceed);
    return () => {
      try {
        if (window.__novasUnsavedChangesGuard) delete window.__novasUnsavedChangesGuard;
      } catch (_) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingNewStudent, hasUnsavedChanges]);



  const [studentData, setStudentData] = useState(() => {
    if (studentId === "new" || isNew) {
      return {
        id: null,
        slug: '',
        name: '',
        email: '',
        phone: '',
        dob: '',
        contactEmail: '',
        contactPhone: '',
        proficiency: 'A1 - Elementary',
        experience: '',
        strengthsWeaknesses: '',
        price: '',
        status: 'active',
        goals: '',
        preferences: '',
        hobbies: '',
        vacationFrom: '',
        vacationTo: '',
        sickLeaveFrom: '',
        sickLeaveTo: '',
        pausedResumeDate: '',
        lessons: [],
        materials: [],
        links: [],
        bannerImage: initialStudentBannerImage,
      };
    }

    const student = getStudentData()[studentId];
    return student ? {
      ...student,
      lessons: student.lessons || [],
    } : {
      id: null,
      slug: 'not-found',
      name: '',
      email: '',
      phone: '',
      dob: '',
      lessons: [],
      materials: [],
      links: [],
      bannerImage: initialStudentBannerImage,
    };
  });

  const openChat = () => {
    const uuid = studentData.id || studentData.studentId;
    if (uuid) markChatRead(uuid);
    setChatModalOpen(true);
  };

  // Opening the chat from the mobile bottom nav: either it dispatches
  // this event directly (teacher was already on this student's profile),
  // or it sent us here via /students?pickForChat=1 which appends ?chat=1
  // to the destination URL once a student is picked.
  useEffect(() => {
    const onOpenMobileChat = (e) => {
      if (e.detail?.studentSlug !== studentId) return;
      const uuid = studentData.id || studentData.studentId;
      if (uuid) markChatRead(uuid);
      setChatModalOpen(true);
    };
    window.addEventListener('novas:openMobileChat', onOpenMobileChat);
    return () => window.removeEventListener('novas:openMobileChat', onOpenMobileChat);
  }, [studentId, studentData]);

  useEffect(() => {
    if (new URLSearchParams(location.search).get('chat') !== '1') return;
    const uuid = studentData.id || studentData.studentId;
    if (!uuid) return;
    markChatRead(uuid);
    setChatModalOpen(true);
    navigate(location.pathname, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, studentData]);

  // Состояние для принудительного обновления статистики
  const [statsUpdateTrigger, setStatsUpdateTrigger] = useState(0);
  const [upcomingLessons, setUpcomingLessons] = useState([]);
  const [allStudentScheduleItems, setAllStudentScheduleItems] = useState([]); // Все уроки из расписания для статистики
  const allStudentScheduleItemsRef = useRef([]);

  const studentDataRef = useRef(studentData);

  // Функция для расчета статистики студента (как в Home)
  const calculateStudentStats = useStudentStats({ studentId, statsUpdateTrigger, allStudentScheduleItems });

  // Refs для проверки обрезки текста в ячейках с деньгами
  const {
    moneyThisMonthRef, plannedIncomeRef,
    isMoneyThisMonthTruncated, isPlannedIncomeTruncated,
  } = useStatCardTruncation({ calculateStudentStats, priceCurrency: studentData.priceCurrency });

  // Обновляем статистику при изменении localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      setStatsUpdateTrigger(prev => prev + 1);
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Также обновляем при изменении localStorage в том же окне
    const originalSetItem = localStorage.setItem;
    localStorage.setItem = function(key, value) {
      originalSetItem.apply(this, arguments);
      if (key === 'novas_lessons_data' || key === 'novas_students_data') {
        handleStorageChange();
      }
    };

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      localStorage.setItem = originalSetItem;
    };
  }, []);

  const [bannerImageUrl, setBannerImageUrl] = useState(initialStudentBannerImage);
  const fileInputRef = useRef(null);
  const [tourOpen, setTourOpen] = useState(false);

  // Auto-open the student tour when the teacher lands on /students/new
  // for the first time. Pauses 700ms so the form mounts.
  useEffect(() => {
    if (studentId !== 'new' && !isNew) return;
    try {
      if (!localStorage.getItem(STUDENT_TOUR_SEEN_KEY)) {
        const t = setTimeout(() => setTourOpen(true), 700);
        return () => clearTimeout(t);
      }
    } catch (_) {}
  }, [studentId, isNew]);

  // Whenever the tour opens, force the Student Details panel open so the
  // tour targets (Name / DOB / Price / etc.) are actually rendered.
  useEffect(() => {
    if (tourOpen) setDetailsOpen(true);
  }, [tourOpen]);

  // Логирование для отладки и проверка инициализации
  useEffect(() => {
    console.log('StudentProfile - bannerImageUrl:', bannerImageUrl);
    console.log('StudentProfile - initialStudentBannerImage:', initialStudentBannerImage);
    console.log('StudentProfile - studentId:', studentId, 'isNew:', isNew);
    
    // Убеждаемся, что для нового студента bannerImageUrl установлен
    if ((studentId === "new" || isNew) && (!bannerImageUrl || bannerImageUrl === '' || bannerImageUrl === 'undefined' || bannerImageUrl === 'null')) {
      console.log('Setting default banner for new student');
      if (initialStudentBannerImage) {
        setBannerImageUrl(initialStudentBannerImage);
      } else {
        console.warn('initialStudentBannerImage is not available');
      }
    }
    
    // Проверяем, что bannerImageUrl валидный
    if (bannerImageUrl && (bannerImageUrl === 'undefined' || bannerImageUrl === 'null')) {
      console.log('Invalid bannerImageUrl detected, resetting to default');
      if (initialStudentBannerImage) {
        setBannerImageUrl(initialStudentBannerImage);
      }
    }
  }, [bannerImageUrl, studentId, isNew]);

  const [activeSectionTab, setActiveSectionTab] = useState('Lessons');
  const {
    homeworkFilter, setHomeworkFilter,
    studentHomework,
    homeworkLoading,
    handleCheckHomework,
    handleDeleteHomework,
  } = useHomeworkManager({ studentId, isNew, navigate });
  const [lessonSearchTerm, setLessonSearchTerm] = useState('');
  const [studentLessons, setStudentLessons] = useState([]);
  // Always-fresh mirrors so window-event handlers can read the latest
  // lists synchronously, without going through a setState updater
  // (whose callback runs in a later React batch, after the surrounding
  // code has finished).
  const studentLessonsRef = useRef([]);
  useEffect(() => { studentLessonsRef.current = studentLessons; }, [studentLessons]);
  const [lessonsLoading, setLessonsLoading] = useState(true);
  const [weekOffset, setWeekOffset] = useState(0); // weekly navigation offset
  const [selectedDayIndex, setSelectedDayIndex] = useState(null); // mobile day-strip selection


  // eslint-disable-next-line no-unused-vars
  const dummyDictionaryEntries = [
    { id: 1, word: "Cat", level: "A1", translation: "Кошка", example: "I have a cat.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
    { id: 2, word: "Animal", level: "A1", translation: "Животное", example: "Both children are real animal lovers.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
    { id: 3, word: "Elephant", level: "A2", translation: "Слон", example: "In the past eight years, the elephant population in Africa has been halved.", lesson: { title: "Talking About Your Last Holiday", thumbnail: "https://via.placeholder.com/40x40/FF5733/FFFFFF?text=L1" } },
  ];
  // Dictionary states moved to DictionaryProfile component

  // Modal states
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customModalConfig, setCustomModalConfig] = useState({});
  const [studentSaveSubmitting, setStudentSaveSubmitting] = useState(false);

  // Cache AI recommendations per student in localStorage so a freshly-opened
  // profile doesn't make the teacher click "Regenerate" (which costs an
  // OpenAI call) every single time. We key by studentId; the entry is
  // overwritten whenever a fresh generation succeeds. Read synchronously
  // from the initializer so the first paint already shows them.
  
  const [showInviteLinkModal, setShowInviteLinkModal] = useState(false);
  

  // Keep studentDataRef current on every render so event handlers always see latest priceAmount
  studentDataRef.current = studentData;

  // ── Payment balance ────────────────────────────────────────────────────────
  // Shared with the student's own Home/Profile views (usePaymentBalance) so
  // both sides reconcile the same way and never drift — this used to be a
  // separate inline copy here, and the two fell out of sync (student saw a
  // stale balance whenever this profile hadn't been opened since their last
  // completed lesson).
  const {
    payments, savePayments, computeLessonCharge,
    paymentBalance, paymentCurrency, paymentsKey,
    paymentsLoading: paymentsFreshLoading,
  } = usePaymentBalance({
    studentId,
    studentSupabaseId: studentData.id || studentData.studentId,
    studentData,
    lessons: studentLessons,
    scheduleItems: allStudentScheduleItems,
    ready: !lessonsLoading,
    // Teacher's own view of a specific student — always read/write Supabase
    // directly. localStorage caching here was showing stale debt/deposit
    // data on localhost (a lingering cache from a previous session/deploy).
    useLocalCache: false,
  });

  // Keep studentData.payments in sync with the hook's own state. Without
  // this, studentData.payments lagged behind every write the hook made
  // (the hook updates its own `payments` state + Supabase, but never the
  // parent's studentData prop) — and since useLocalCache:false makes
  // studentData.payments the hook's read-back source, the pre-existing
  // 3s polling interval below (handleStorageChange) kept re-triggering
  // usePaymentBalance's reconciliation effect against that stale
  // snapshot, causing it to "fix" the same drift over and over and write
  // to Supabase repeatedly (visible as the payments array flapping in
  // size on every poll).
  useEffect(() => {
    setStudentData(prev => (prev.payments === payments ? prev : { ...prev, payments }));
  }, [payments]);

  // handleLessonStatusUpdate (below) is registered once per studentId via a
  // window listener and used to read payments through loadPayments(), which
  // can return a stale snapshot right after a remount (initial studentData
  // comes from the novas_students_data localStorage cache, which is never
  // updated on deposit/charge — only Supabase and this ref are). Reading
  // this ref instead ties it directly to the hook's own up-to-date state.
  const paymentsRef = useRef(payments);
  useEffect(() => { paymentsRef.current = payments; }, [payments]);


  // Keep a fresh mirror for the listener (which reads this synchronously).
  useEffect(() => {
    allStudentScheduleItemsRef.current = allStudentScheduleItems;
  }, [allStudentScheduleItems]);

  const {
    showPaymentModal, setShowPaymentModal,
    paymentDepositInput, setPaymentDepositInput,
    paymentDepositNote, setPaymentDepositNote,
    paymentChargeInput, setPaymentChargeInput,
    paymentChargeNote, setPaymentChargeNote,
    addPaymentDeposit,
    addPaymentCharge,
    deletePayment,
    lessonCountLabel,
    paidLessons,
    chargedLessons,
  } = usePaymentModalActions({
    payments, savePayments, priceAmount: studentData.priceAmount, paymentBalance,
  });

  const showCustomModalConfig = (config) => {
    setCustomModalConfig(config);
    setShowCustomModal(true);
  };

  const hideCustomModal = () => {
    setShowCustomModal(false);
  };

  const {
    newMaterialLink, setNewMaterialLink,
    newMaterialFile,
    materialType, setMaterialType,
    materialFileInputRef,
    materialSearchTerm, setMaterialSearchTerm,
    editingMaterialId, editingMaterialName, setEditingMaterialName,
    previewMaterial,
    isDragOver,
    filteredMaterials,
    handleAddLink,
    handleFileChange,
    handleDragOver,
    handleDragEnter,
    handleDrop,
    handleDragLeave,
    handleDeleteMaterial,
    handleEditMaterialName,
    handleSaveMaterialName,
    handleCancelEditMaterial,
    handleMaterialPreview,
    handleClosePreview,
  } = useMaterialsManager({ studentData, setStudentData, showCustomModalConfig, setShowCustomModal });

  const {
    deletingLessonId,
    handleStartLesson,
    handleDeleteLesson,
    handleCopyLesson,
    confirmCopyLesson,
    showCopyModal, setShowCopyModal,
    lessonToCopy,
    copyTargetStudent, setCopyTargetStudent,
    copyDate, setCopyDate,
    copyModalStudents,
    copyLessonSubmitting,
  } = useLessonsManager({ studentId, isNew, navigate, setStudentLessons, showCustomModalConfig, setShowCustomModal });

  const { aiRecommendations, aiLoading, aiError, generateAIRecommendations } = useAIRecommendations({ studentId, studentData });

  const handleModalConfirm = () => {
    if (customModalConfig.onConfirm) {
      customModalConfig.onConfirm();
    }
    hideCustomModal();
  };

  // Dictionary loading moved to DictionaryProfile component

  // Проверяем, обрезается ли текст в ячейках с деньгами
  
  // Dictionary update listener moved to DictionaryProfile component


  // Listen for student data updates

  // Слушаем изменения статуса урока из Home или Schedule
  const { updateUpcomingLessons } = useScheduleSync({
    studentId, isNew, setStudentLessons, studentLessonsRef, allStudentScheduleItemsRef,
    setAllStudentScheduleItems, setUpcomingLessons, paymentsKey, paymentsRef,
    studentDataRef, computeLessonCharge, savePayments,
  });

  useStudentProfileLoader({
    studentId, isNew, bannerImageUrl, setBannerImageUrl,
    setStudentData, setStudentLessons, setLessonsLoading, updateUpcomingLessons,
  });

  const {
    showAddLessonModal, setShowAddLessonModal,
    selectedDateForLesson, setSelectedDateForLesson,
    addLessonItemType, setAddLessonItemType,
    addLessonTitle, setAddLessonTitle,
    addLessonFrom, setAddLessonFrom,
    addLessonTo, setAddLessonTo,
    addLessonRepeatWeekly, setAddLessonRepeatWeekly,
    addLessonAllDay, setAddLessonAllDay,
    addLessonErrors, setAddLessonErrors,
    addLessonSaving,
    showEditScheduleLessonModal, setShowEditScheduleLessonModal,
    editingScheduleLesson, setEditingScheduleLesson,
    editScheduleLessonTitle, setEditScheduleLessonTitle,
    editScheduleLessonDate, setEditScheduleLessonDate,
    editScheduleLessonFrom, setEditScheduleLessonFrom,
    editScheduleLessonTo, setEditScheduleLessonTo,
    editScheduleLessonAllDay, setEditScheduleLessonAllDay,
    editScheduleLessonStatus, setEditScheduleLessonStatus,
    statusChangeLoading, setStatusChangeLoading,
    editScheduleLessonSaving,
    deleteScheduleLessonSaving,
    updateToTime,
    handleAddLessonToSchedule,
    handleSaveScheduleLesson,
    handleDeleteScheduleLesson,
  } = useScheduleLessonModals({
    studentId, isNew, studentData, showCustomModalConfig, updateUpcomingLessons,
    setStudentLessons, paymentsKey, paymentsRef, studentDataRef, savePayments,
  });

  // Добавляем эффект для обновления уроков при изменении localStorage
  useEffect(() => {
    if (studentId && studentId !== "new" && !isNew) {
      const handleStorageChange = () => {
        // Используем правильный ID студента из данных
        const currentStudent = getStudentData()[studentId];
        const studentIdForLessons = currentStudent?.studentId || currentStudent?.id || studentId;
        if (!studentIdForLessons) return;
        
        getLessonListForStudent(studentIdForLessons).then(lessons => {
        setStudentLessons(lessons);
        updateUpcomingLessons(); // Обновляем upcoming lessons
        }).catch(error => {
          console.error('Error loading lessons:', error);
        });
      };

      // Слушаем изменения в localStorage (другие вкладки/окна — единственный
      // случай, который отсюда реально нужен: смена статуса урока в ЭТОМ же
      // окне уже сама обновляет studentLessons и рассылает свои события
      // сразу, без ожидания). Раньше здесь был ещё setInterval(handleStorageChange,
      // 3000) "на всякий случай" — он создавал новые ссылки на массивы
      // lessons/scheduleItems каждые 3с, из-за чего usePaymentBalance
      // постоянно перезапускал реконсиляцию на устаревших данных: это
      // вызывало и мельтешение автосписаний, и откат вручную добавленных
      // платежей гонкой с этим же опросом. Убрано.
      window.addEventListener('storage', handleStorageChange);

      // Инициализируем upcoming lessons
      updateUpcomingLessons();

      // AI recommendations on mount: only run if profile already has
      // meaningful content. Otherwise wait for the teacher to click
      // "Regenerate" so we don't burn a call on every page open.

      return () => {
        window.removeEventListener('storage', handleStorageChange);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, isNew]);

  const proficiencyLevels = [
    "A1 - Elementary",
    "A2 - Pre-Intermediate",
    "A2+ - Pre-Intermediate+",
    "B1 - Intermediate",
    "B1+ - Intermediate+",
    "B2 - Upper-Intermediate",
    "B2+ - Upper-Intermediate+",
    "C1 - Advanced",
    "C1+ - Advanced+",
    "C2 - Proficient"
  ];

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "on_vacation", label: "On Vacation" },
    { value: "sick_leave", label: "Sick Leave" },
    { value: "paused", label: "Paused" }
  ];

  const {
    handleFieldChange,
    handleStatusChange,
    handleImageUpload,
    handleBannerUploadClick,
    handleSubmit,
    handleDeleteProfile,
  } = useProfileFormActions({
    studentId, isNew, navigate, currentUser,
    studentData, setStudentData, markDirty,
    validationErrors, setValidationErrors,
    bannerImageUrl, setBannerImageUrl, fileInputRef,
    studentSaveSubmitting, setStudentSaveSubmitting,
    setHasUnsavedChanges, setShowInviteLinkModal,
    showCustomModalConfig,
  });


  // Основная таблица lessons показывает только полноценные уроки из studentLessons
  // Уроки из расписания (allStudentScheduleItems) отображаются только в календаре upcoming lessons
  const filteredLessons = studentLessons.filter(lesson =>
    (lesson.title || '').toLowerCase().includes(lessonSearchTerm.toLowerCase())
  );

  // Sort so latest saved/updated lessons appear first (fallbacks: createdAt/date/id)
  const sortedLessons = React.useMemo(() => {
    const lessonsCopy = [...filteredLessons];
    const toTimestamp = (lesson) => {
      const primary = lesson.updatedAt || lesson.savedAt || lesson.modifiedAt || lesson.createdAt;
      if (primary) {
        const t = Date.parse(primary);
        if (!Number.isNaN(t)) return t;
      }
      if (lesson.date) {
        const s = String(lesson.date);
        const parts = s.split(/[./-]/);
        if (parts.length >= 3) {
          const [a, b, c] = parts;
          const year = parseInt(c, 10) < 100 ? parseInt(c, 10) + 2000 : parseInt(c, 10);
          let day = parseInt(a, 10);
          let month = parseInt(b, 10);
          if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day) && month >= 1 && month <= 12) {
            const d1 = new Date(year, month - 1, day).getTime();
            if (!Number.isNaN(d1)) return d1;
          }
          // Try swapped dd/mm vs mm/dd
          day = parseInt(b, 10);
          month = parseInt(a, 10);
          if (!Number.isNaN(year) && !Number.isNaN(month) && !Number.isNaN(day) && month >= 1 && month <= 12) {
            const d2 = new Date(year, month - 1, day).getTime();
            if (!Number.isNaN(d2)) return d2;
          }
        }
        const t2 = Date.parse(s);
        if (!Number.isNaN(t2)) return t2;
      }
      const idNum = Number(lesson.id);
      return Number.isNaN(idNum) ? 0 : idNum;
    };
    // Done lessons sink to the bottom regardless of recency — they're no
    // longer actionable, so keeping them out of the way of in-progress/
    // upcoming ones matters more than their save/update time here.
    lessonsCopy.sort((a, b) => {
      const aDone = a.status === 'done' ? 1 : 0;
      const bDone = b.status === 'done' ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      return toTimestamp(b) - toTimestamp(a);
    });
    return lessonsCopy;
  }, [filteredLessons]);

  const {
    handleLessonStatusChange: handleScheduleLessonStatusChange,
    openEditScheduleLessonModal,
    handleAddLesson,
  } = useScheduleTabActions({
    studentId, isNew, upcomingLessons, studentLessons, setStudentLessons,
    setStatusChangeLoading, updateUpcomingLessons, setStudentData,
    setEditingScheduleLesson, setEditScheduleLessonTitle, setEditScheduleLessonDate,
    setEditScheduleLessonFrom, setEditScheduleLessonTo, setEditScheduleLessonAllDay,
    setEditScheduleLessonStatus, setShowEditScheduleLessonModal,
    setSelectedDateForLesson, setAddLessonItemType, setAddLessonTitle,
    setAddLessonFrom, setAddLessonTo, setAddLessonRepeatWeekly, setAddLessonAllDay,
    setAddLessonErrors, setShowAddLessonModal,
  });

  const renderUpcomingLessons = () => {
    // Data-shaping lives in computeWeekSchedule (StudentProfile/scheduleWeek.js)
    // — a pure function, split out on purpose so it's testable without
    // rendering this whole component.
    const studentIdForLessons = getStudentData()[studentId]?.studentId || getStudentData()[studentId]?.id || studentId;
    const { weekDays, lessonsByDay } = computeWeekSchedule({
      weekOffset,
      upcomingLessons,
      studentLessons,
      studentId,
      localLessons: getLessonsFromLocalStorage(),
      studentIdForLessons,
    });

    return (
      <ScheduleTab
        weekDays={weekDays}
        lessonsByDay={lessonsByDay}
        statusChangeLoading={statusChangeLoading}
        onPrevWeek={() => setWeekOffset(o => o - 1)}
        onNextWeek={() => setWeekOffset(o => o + 1)}
        studentName={studentData.name}
        onLessonStatusChange={handleScheduleLessonStatusChange}
        onEditLesson={openEditScheduleLessonModal}
        onAddLesson={handleAddLesson}
        selectedDayIndex={selectedDayIndex}
        onSelectDay={setSelectedDayIndex}
      />
    );
  };


  const renderLessonsSection = () => (
    <LessonsTab
      searchTerm={lessonSearchTerm}
      onSearchTermChange={setLessonSearchTerm}
      onCreateLesson={() => {
        const goToLessonEditor = () => navigate(`/lessons/new?studentId=${studentId}`);
        if (window.innerWidth <= 768) {
          showCustomModalConfig({
            title: 'Create on desktop for best results',
            message: "We strongly recommend building lessons on a desktop or laptop. The editor's drag-and-drop blocks, multi-column layouts, and live previews are hard to line up precisely on a small touchscreen, so lessons built on mobile often end up rougher than they'd be otherwise.",
            type: 'warning',
            confirmText: 'Continue anyway',
            cancelText: 'Cancel',
            showCancel: true,
            onConfirm: goToLessonEditor,
          });
        } else {
          goToLessonEditor();
        }
      }}
      lessonsLoading={lessonsLoading}
      sortedLessons={sortedLessons}
      deletingLessonId={deletingLessonId}
      onStartLesson={handleStartLesson}
      onLessonStatusChange={handleLessonStatusChange}
      onCopyLesson={handleCopyLesson}
      onDeleteLesson={handleDeleteLesson}
    />
  );

  const renderHomeworkSection = () => (
    <HomeworkTab
      homeworkFilter={homeworkFilter}
      onFilterChange={setHomeworkFilter}
      homeworkLoading={homeworkLoading}
      homework={studentHomework}
      onCheckHomework={handleCheckHomework}
      onDeleteHomework={handleDeleteHomework}
    />
  );

  const renderMaterialsSection = () => (
    <MaterialsTab
      materialType={materialType}
      onMaterialTypeChange={setMaterialType}
      newMaterialLink={newMaterialLink}
      onNewMaterialLinkChange={setNewMaterialLink}
      onAddLink={handleAddLink}
      isDragOver={isDragOver}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onFileChange={handleFileChange}
      materialFileInputRef={materialFileInputRef}
      newMaterialFile={newMaterialFile}
      onChooseFile={() => materialFileInputRef.current && materialFileInputRef.current.click()}
      materialSearchTerm={materialSearchTerm}
      onMaterialSearchTermChange={setMaterialSearchTerm}
      filteredMaterials={filteredMaterials}
      editingMaterialId={editingMaterialId}
      editingMaterialName={editingMaterialName}
      onEditingMaterialNameChange={setEditingMaterialName}
      onSaveMaterialName={handleSaveMaterialName}
      onCancelEditMaterial={handleCancelEditMaterial}
      onMaterialPreview={handleMaterialPreview}
      onEditMaterialName={handleEditMaterialName}
      onDeleteMaterial={handleDeleteMaterial}
    />
  );

  // Dictionary functions moved to DictionaryProfile component

  const { handleCreateLesson, handleLessonStatusChange } = useLessonsTabStatusChange({
    studentId, navigate, studentLessons, setStudentLessons, setStatusChangeLoading, updateUpcomingLessons,
  });

  // Функция для автоматического обновления времени окончания


  return (
    <div className={`student-profile-page${detailsOpen ? ' details-open' : ''}`}>
      {studentSaveSubmitting && (
        <div className="student-save-overlay" role="status" aria-live="polite" aria-label="Saving student">
          <div className="student-save-overlay__center">
            <div className="student-save-overlay__spinner" aria-hidden />
            <div className="student-save-overlay__title">Saving student</div>
          </div>
        </div>
      )}
      <div
        className="profile-banner"
        style={{ 
          backgroundImage: (bannerImageUrl || initialStudentBannerImage) ? `url(${bannerImageUrl || initialStudentBannerImage})` : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#f0f0f0' // Fallback цвет если изображение не загрузится
        }}
      >
        {/* Скрытый img для проверки загрузки баннера */}
        <img
          src={bannerImageUrl || initialStudentBannerImage}
          alt=""
          style={{ display: 'none' }}
          onError={(e) => {
            console.error('Error loading banner:', bannerImageUrl || initialStudentBannerImage);
            const bannerPath = bannerImageUrl || initialStudentBannerImage;
            if (bannerPath && typeof bannerPath === 'string' && !bannerPath.startsWith('http') && !bannerPath.startsWith('data:')) {
              // Пробуем альтернативный путь
              const altPath = bannerPath.startsWith('/') ? bannerPath : `/static/media/${bannerPath.split('/').pop()}`;
              console.log('Trying alternative banner path:', altPath);
              // Обновляем backgroundImage родительского div
              e.target.parentElement.style.backgroundImage = `url(${altPath})`;
              e.target.src = altPath; // Пробуем загрузить альтернативный путь
            } else {
              // Если альтернативный путь тоже не работает, используем дефолтный
              if (initialStudentBannerImage && bannerPath !== initialStudentBannerImage) {
                console.log('Using fallback initialStudentBannerImage');
                e.target.parentElement.style.backgroundImage = `url(${initialStudentBannerImage})`;
              }
            }
          }}
          onLoad={() => {
            console.log('Banner loaded successfully:', bannerImageUrl || initialStudentBannerImage);
          }}
        />
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
          accept="image/*"
        />
        <div className="banner-top-left-actions">
          <button
            className="back-button"
            onClick={() => {
              confirmLoseChanges(() => navigate("/students"));
            }}
          >
            <span className="back-button-icon">
              <svg width="24" height="24" viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="m97.499855 59.5758209c0 14.2470169-11.5494766 25.7964935-25.7964935 25.7964935h-14.4460335c-2.3745003 0-4.2994156-1.9249115-4.2994156-4.2994156 0-2.3744965 1.9249153-4.2994156 4.2994156-4.2994156h14.2310677c9.4980011 0 17.1976624-7.6996536 17.1976624-17.1976624s-7.6996613-17.1976624-17.1976624-17.1976624h-52.9258137l13.7151318 11.5224342c1.816494 1.5315552 2.0474854 4.2456856.5159302 6.0621758-1.5315495 1.8164978-4.2456799 2.0474892-6.0621738.515934l-22.7009121-19.0464133c-1.8164933-1.5292511-2.0493481-4.2415123-.5200972-6.0580063.1581917-.1879044.3321936-.361908.5200973-.5200996l22.7009163-19.2183876c1.8283691-1.5315533 4.5521183-1.2909393 6.0836735.5374269s1.2909393 4.5521183-.5374298 6.0836735l-13.7151362 11.4364443h52.9258118c14.2465134-.1188049 25.8919067 11.3339691 26.010704 25.5804825.0008488.1006698.0010929.2013283.0007572.3019981z"/>
              </svg>
            </span>
            <span className="back-button-text">Return</span>
          </button>
          <button
            type="button"
            className="student-profile-banner-save banner-action"
            data-tour="student-save"
            onClick={handleSubmit}
            aria-label="Save Profile"
            disabled={studentSaveSubmitting}
          >
            <span className="banner-action-icon" aria-hidden="true">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>
              </svg>
            </span>
            <span className="banner-action-text">{studentSaveSubmitting ? 'Saving…' : 'Save'}</span>
          </button>
          {studentId !== "new" && (
            <button 
              type="button" 
              className="student-profile-banner-delete banner-action" 
              onClick={handleDeleteProfile}
              aria-label="Delete Profile"
            >
              <span className="banner-action-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8z"/>
                </svg>
              </span>
              <span className="banner-action-text">Delete</span>
            </button>
          )}
          <button className="student-profile-banner-change-cover" onClick={handleBannerUploadClick}>
            <span className="student-profile-banner-change-cover-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 6H17.82L15.39 3.59C15.01 3.22 14.52 3 14 3H10C9.48 3 8.99 3.22 8.62 3.59L6.18 6H3C1.9 6 1 6.9 1 8V20C1 21.1 1.9 22 3 22H21C22.1 22 23 21.1 23 20V8C23 6.9 22.1 6 21 6ZM12 18C9.79 18 8 16.21 8 14C8 11.79 9.79 10 12 10C14.21 10 16 11.79 16 14C16 16.21 14.21 18 12 18Z"/>
              </svg>
            </span>
            <span className="student-profile-banner-change-cover-text">Change Cover</span>
          </button>
        </div>
        <h2 className="student-name-banner-input">{studentData.name || 'Student'}</h2>
        {/* Balance widget — top right of banner. The button's own label
         * IS the balance now (amount + lesson count) instead of sitting
         * next to a separate "Details" button — still opens the exact
         * same payment modal on click. */}
        {studentId !== 'new' && !isNew && (
          <div className="banner-balance-widget" onClick={e => e.stopPropagation()}>
            <button
              className="banner-balance-btn"
              onClick={() => setShowPaymentModal(true)}
            >
              {paymentsFreshLoading ? (
                // Fresh balance hasn't come back from Supabase yet — show
                // a loading placeholder instead of the (possibly stale,
                // cached) value for an instant before swapping it out.
                <span className="banner-balance-amount banner-balance-loading" aria-label="Loading balance">
                  <span className="banner-balance-loading-dot" />
                  <span className="banner-balance-loading-dot" />
                  <span className="banner-balance-loading-dot" />
                </span>
              ) : (
                <>
                  <span className={`banner-balance-amount ${paymentBalance >= 0 ? 'positive' : 'negative'}`}>
                    {paymentBalance < 0 && '⚠ '}
                    {paymentBalance >= 0 ? '+' : ''}{paymentCurrency}{Math.abs(paymentBalance).toFixed(2)}
                  </span>
                  {lessonCountLabel && (
                    <span className={`banner-balance-lessons ${paymentBalance >= 0 ? 'positive' : 'negative'}`}>
                      {lessonCountLabel}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        )}

      </div>

      {/* ── Payment details modal ─────────────────────────────────────────── */}
      <PaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentBalance={paymentBalance}
        paymentCurrency={paymentCurrency}
        lessonCountLabel={lessonCountLabel}
        paidLessons={paidLessons}
        chargedLessons={chargedLessons}
        payments={payments}
        depositAmount={paymentDepositInput}
        onDepositAmountChange={setPaymentDepositInput}
        depositNote={paymentDepositNote}
        onDepositNoteChange={setPaymentDepositNote}
        onAddDeposit={addPaymentDeposit}
        chargeAmount={paymentChargeInput}
        onChargeAmountChange={setPaymentChargeInput}
        chargeNote={paymentChargeNote}
        onChargeNoteChange={setPaymentChargeNote}
        onAddCharge={addPaymentCharge}
        defaultChargeAmount={studentData.priceAmount}
        onDeletePayment={deletePayment}
      />

      <ProfileDetailsForm
        detailsOpen={detailsOpen}
        onToggleDetails={() => setDetailsOpen(v => !v)}
        onShowInviteLink={() => setShowInviteLinkModal(true)}
        studentData={studentData}
        onFieldChange={handleFieldChange}
        onStatusChange={handleStatusChange}
        proficiencyLevels={proficiencyLevels}
        statusOptions={statusOptions}
        validationErrors={validationErrors}
        aiLoading={aiLoading}
        aiError={aiError}
        aiRecommendations={aiRecommendations}
        onGenerateAIRecommendations={generateAIRecommendations}
      />

      {/* Floating action buttons - fixed to viewport */}
      <div className="fab-stack">
            <div className="fab-col">
              <button
                className="fab-btn chat"
                title="Chat Now"
                onClick={openChat}
              >
                {(unreadByStudent[studentData.id || studentData.studentId]?.chat || 0) > 0 && (
                  <span className="fab-unread-badge">
                    {unreadByStudent[studentData.id || studentData.studentId].chat > 99 ? '99+' : unreadByStudent[studentData.id || studentData.studentId].chat}
                  </span>
                )}
                <svg fill="none" height="24" viewBox="0 0 64 64" width="24" xmlns="http://www.w3.org/2000/svg">
                  <path clip-rule="evenodd" d="m2 16c0-7.73199 6.26801-14 14-14h32c7.732 0 14 6.26801 14 14v44c0 .7523-.4221 1.4409-1.0924 1.7822-.6704.3414-1.4756.2777-2.0839-.1647l-10.4741-7.6175h-32.3496c-7.73199 0-14-6.268-14-14zm15 2c-1.1046 0-2 .8954-2 2s.8954 2 2 2h30c1.1046 0 2-.8954 2-2s-.8954-2-2-2zm0 12c-1.1046 0-2 .8954-2 2s.8954 2 2 2h12c1.1046 0 2-.8954 2-2s-.8954-2-2-2z" fill="currentColor" fill-rule="evenodd"/>
                </svg>
              </button>
              <button className="fab-btn invite-link" title="Invite Link" onClick={() => setShowInviteLinkModal(true)}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                </svg>
              </button>
            </div>
            <button className="fab-main create-lesson-btn" onClick={handleCreateLesson}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
        </div>

      {/* Student Stats Overview */}
      <div className="student-stats-overview section-card">
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{calculateStudentStats.lessonsThisWeek}</p>
              <div className="stat-text">
                <div className="stat-text-line1">Lessons</div>
                <div className="stat-text-line2">this week</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-content">
              <p className="stat-value">{calculateStudentStats.lessonsThisMonth}</p>
              <div className="stat-text">
                <div className="stat-text-line1">Lessons</div>
                <div className="stat-text-line2">this month</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-content">
              <p 
                ref={moneyThisMonthRef}
                className={`stat-value ${isMoneyThisMonthTruncated ? 'truncated' : ''}`}
                title={`${(studentData.priceCurrency || '$')}${(calculateStudentStats.moneyThisMonth || 0).toLocaleString()}`}
              >
                {(studentData.priceCurrency || '$')}{(calculateStudentStats.moneyThisMonth || 0).toLocaleString()}
              </p>
              <div className={`stat-text ${isMoneyThisMonthTruncated ? 'hide-on-hover' : ''}`}>
                <div className="stat-text-line1">Money</div>
                <div className="stat-text-line2">this month</div>
              </div>
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-content">
              <p 
                ref={plannedIncomeRef}
                className={`stat-value ${isPlannedIncomeTruncated ? 'truncated' : ''}`}
                title={`${(studentData.priceCurrency || '$')}${(calculateStudentStats.plannedIncome || 0).toLocaleString()}`}
              >
                {(studentData.priceCurrency || '$')}{(calculateStudentStats.plannedIncome || 0).toLocaleString()}
              </p>
              <div className={`stat-text ${isPlannedIncomeTruncated ? 'hide-on-hover' : ''}`}>
                <div className="stat-text-line1">Planned</div>
                <div className="stat-text-line2">income</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="lessons-dictionary-materials-section">
        <div className="section-tabs-navigation">
          <button
            className={`section-tab-button ${activeSectionTab === 'Lessons' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Lessons')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 19.5C4 18.1193 5.11929 17 6.5 17H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M6.5 2H20V22H6.5C5.11929 22 4 20.8807 4 19.5V4.5C4 3.11929 5.11929 2 6.5 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Lessons
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Homework' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Homework')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
              <polyline points="9,22 9,12 15,12 15,22"/>
            </svg>
            Homework
            {(unreadByStudent[studentData.id || studentData.studentId]?.homework || 0) > 0 && (
              <span className="section-tab-unread-badge">
                {unreadByStudent[studentData.id || studentData.studentId].homework > 99 ? '99+' : unreadByStudent[studentData.id || studentData.studentId].homework}
              </span>
            )}
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Dictionary' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Dictionary')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Dictionary
          </button>
          <button
            className={`section-tab-button ${activeSectionTab === 'Materials' ? 'active' : ''}`}
            onClick={() => setActiveSectionTab('Materials')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M22 19C22 19.5304 21.7893 20.0391 21.4142 20.4142C21.0391 20.7893 20.5304 21 20 21H4C3.46957 21 2.96086 20.7893 2.58579 20.4142C2.21071 20.0391 2 19.5304 2 19V5C2 4.46957 2.21071 3.96086 2.58579 3.58579C2.96086 3.21071 3.46957 3 4 3H9L11 6H20C20.5304 6 21.0391 6.21071 21.4142 6.58579C21.7893 6.96086 22 7.46957 22 8V19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Materials
          </button>
        </div>

        <div className="section-content">
          {activeSectionTab === 'Lessons' && renderLessonsSection()}
          {activeSectionTab === 'Homework' && renderHomeworkSection()}
          {activeSectionTab === 'Dictionary' && <DictionaryProfile studentId={studentId} onError={showCustomModalConfig} />}
          {activeSectionTab === 'Materials' && renderMaterialsSection()}
        </div>
      </div>

      <div className="supplementary-sections-container">
        <div className="section-card upcoming-lessons-card">
          {renderUpcomingLessons()}
        </div>
      </div>

      {/* Moved Save/Delete to banner; removing bottom form actions */}

      {/* Custom Modal */}
      <CustomModal
        show={showCustomModal}
        config={customModalConfig}
        onClose={hideCustomModal}
        onConfirm={handleModalConfirm}
      />


      {/* Homework Modal */}

      {/* Copy Lesson Modal */}
      <CopyLessonModal
        show={showCopyModal}
        lessonToCopy={lessonToCopy}
        copyTargetStudent={copyTargetStudent}
        onTargetStudentChange={setCopyTargetStudent}
        copyDate={copyDate}
        onDateChange={setCopyDate}
        copyModalStudents={copyModalStudents}
        submitting={copyLessonSubmitting}
        onClose={() => setShowCopyModal(false)}
        onConfirm={confirmCopyLesson}
      />


      {/* Add Lesson to Schedule Modal (как в Schedule.js) */}
      <AddLessonModal
        show={showAddLessonModal && !!selectedDateForLesson}
        selectedDate={selectedDateForLesson}
        itemType={addLessonItemType}
        onItemTypeChange={setAddLessonItemType}
        errors={addLessonErrors}
        from={addLessonFrom}
        onFromChange={(newFrom) => {
          setAddLessonFrom(newFrom);
          // Автоматически устанавливаем время окончания на час позже
          setAddLessonTo(updateToTime(newFrom));
        }}
        to={addLessonTo}
        onToChange={setAddLessonTo}
        studentName={studentData.name}
        title={addLessonTitle}
        onTitleChange={setAddLessonTitle}
        repeatWeekly={addLessonRepeatWeekly}
        onRepeatWeeklyChange={setAddLessonRepeatWeekly}
        allDay={addLessonAllDay}
        onAllDayChange={setAddLessonAllDay}
        saving={addLessonSaving}
        onCancel={() => setShowAddLessonModal(false)}
        onSubmit={handleAddLessonToSchedule}
      />


      {/* Edit Schedule Lesson Modal */}
      <EditScheduleLessonModal
        show={showEditScheduleLessonModal && !!editingScheduleLesson}
        title={editScheduleLessonTitle}
        onTitleChange={setEditScheduleLessonTitle}
        date={editScheduleLessonDate}
        onDateChange={setEditScheduleLessonDate}
        allDay={editScheduleLessonAllDay}
        onAllDayChange={setEditScheduleLessonAllDay}
        from={editScheduleLessonFrom}
        onFromChange={(newFrom) => {
          setEditScheduleLessonFrom(newFrom);
          // Автоматически устанавливаем время окончания на час позже
          const [hours, minutes] = newFrom.split(':').map(Number);
          const endHours = hours + 1;
          setEditScheduleLessonTo(`${endHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`);
        }}
        to={editScheduleLessonTo}
        onToChange={setEditScheduleLessonTo}
        status={editScheduleLessonStatus}
        onStatusChange={setEditScheduleLessonStatus}
        editSaving={editScheduleLessonSaving}
        deleteSaving={deleteScheduleLessonSaving}
        onCancel={() => {
          setShowEditScheduleLessonModal(false);
          setEditingScheduleLesson(null);
          setEditScheduleLessonTitle('');
          setEditScheduleLessonDate('');
          setEditScheduleLessonFrom('');
          setEditScheduleLessonTo('');
          setEditScheduleLessonAllDay(false);
          setEditScheduleLessonStatus('in_progress');
        }}
        onDelete={handleDeleteScheduleLesson}
        onSave={handleSaveScheduleLesson}
      />


      {/* Material Preview Modal */}
      <MaterialPreviewModal
        material={previewMaterial}
        onClose={handleClosePreview}
      />


      {/* Invite Link Modal */}
      <InviteLinkModal
        show={showInviteLinkModal}
        inviteLink={studentData.inviteLink}
        isRegistered={studentData.isRegistered}
        onClose={() => setShowInviteLinkModal(false)}
        onCopy={() => {
          navigator.clipboard.writeText(studentData.inviteLink);
          showCustomModalConfig({
            title: 'Link Copied!',
            message: 'Student invite link has been copied to clipboard.',
            type: 'success',
            showCancel: false,
            confirmText: 'OK'
          });
        }}
      />
      <StudentChatModal
        isOpen={chatModalOpen}
        onClose={() => setChatModalOpen(false)}
        studentId={studentId}
        studentName={studentData?.name || ''}
        senderName={currentUser?.name || ''}
        isStudent={false}
      />

      <TourTrigger
        onClick={() => setTourOpen(true)}
        pulsing={!tourOpen && (() => { try { return !localStorage.getItem(STUDENT_TOUR_SEEN_KEY); } catch (_) { return false; } })()}
        label="Tour"
        tall
      />
      <Tour
        steps={STUDENT_TOUR_STEPS}
        isOpen={tourOpen}
        onClose={() => {
          setTourOpen(false);
          try { localStorage.setItem(STUDENT_TOUR_SEEN_KEY, '1'); } catch (_) {}
        }}
        onComplete={() => {
          try { localStorage.setItem(STUDENT_TOUR_SEEN_KEY, '1'); } catch (_) {}
        }}
      />
    </div>
  );
}

export default StudentProfile; 